"use client";

import { useState } from "react";

type FaqItem = { q: string; a: string };

/**
 * Pricing-page FAQ list with single-open accordion behaviour plus a
 * "Show N more" / "Show less" toggle. Mirrors LandingFaq so accordion
 * UX is identical across the marketing site — opening one question
 * collapses the others, and the first question is open on mount.
 */
export function PricingFaq({
  items,
  defaultVisibleCount = 5,
}: {
  items: FaqItem[];
  defaultVisibleCount?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [openQuestion, setOpenQuestion] = useState<string | null>(
    () => items[0]?.q ?? null,
  );
  const hiddenCount = Math.max(0, items.length - defaultVisibleCount);
  const visibleItems = expanded ? items : items.slice(0, defaultVisibleCount);
  const defaultOpenQuestion = items[0]?.q ?? null;

  function toggleExpanded() {
    setExpanded((prev) => {
      const nextExpanded = !prev;
      // If the user collapses back to the short list and the currently-open
      // question is no longer visible, snap the open state back to the first
      // question so the list never renders fully closed.
      if (
        !nextExpanded &&
        !items
          .slice(0, defaultVisibleCount)
          .some((item) => item.q === openQuestion)
      ) {
        setOpenQuestion(defaultOpenQuestion);
      }
      return nextExpanded;
    });
  }

  return (
    <>
      <div className="pricing-faq-list">
        {visibleItems.map((item) => (
          <details
            key={item.q}
            className="pricing-faq-item"
            open={openQuestion === item.q}
          >
            <summary
              onClick={(event) => {
                event.preventDefault();
                setOpenQuestion((current) =>
                  current === item.q ? null : item.q,
                );
              }}
            >
              <span>{item.q}</span>
            </summary>
            <div className="pricing-faq-answer">
              <p>{item.a}</p>
            </div>
          </details>
        ))}
      </div>

      {hiddenCount > 0 ? (
        <button
          type="button"
          className="pricing-faq-toggle"
          onClick={toggleExpanded}
          aria-expanded={expanded}
        >
          {expanded
            ? "Show less"
            : `Show ${hiddenCount} more question${hiddenCount === 1 ? "" : "s"}`}
        </button>
      ) : null}
    </>
  );
}
